import { addDays, nextDay, setHours, setMinutes, parseISO, getDay } from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
import { REQUIRED_VARIABLES } from "@/constants/reminderTemplates";

export interface TemplateVariables {
  first_name: string;
  last_name: string;
  full_name: string;
  check_in_name: string;
  check_in_link: string;
  due_date: string;
  due_time: string;
  participant_timezone: string;
  organization_name: string;
  reminder_sender_name: string;
}

// Replace variables in a template string
export function replaceVariables(template: string, variables: TemplateVariables): string {
  let result = template;
  
  Object.entries(variables).forEach(([key, value]) => {
    const placeholder = `{{${key}}}`;
    result = result.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
  });
  
  return result;
}

// Validate that a template contains all required variables
export function validateTemplate(template: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Check for required variables
  REQUIRED_VARIABLES.forEach(variable => {
    if (!template.includes(variable)) {
      errors.push(`Template must include ${variable}`);
    }
  });
  
  // Check for malformed variables (unclosed braces)
  const openBraces = (template.match(/\{\{/g) || []).length;
  const closeBraces = (template.match(/\}\}/g) || []).length;
  
  if (openBraces !== closeBraces) {
    errors.push('Template contains malformed variables (mismatched braces)');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

// Extract all variables from a template
export function extractVariables(template: string): string[] {
  const variableRegex = /\{\{([^}]+)\}\}/g;
  const variables: string[] = [];
  let match;
  
  while ((match = variableRegex.exec(template)) !== null) {
    variables.push(`{{${match[1]}}}`);
  }
  
  return [...new Set(variables)]; // Remove duplicates
}

// Helper function to format timezone into abbreviated form with offset
export function formatTimezone(timezone: string): string {
  try {
    // Timezone abbreviation mappings for common timezones
    const timezoneMap: Record<string, string> = {
      'America/New_York': 'EST',
      'America/Chicago': 'CST',
      'America/Denver': 'MST',
      'America/Phoenix': 'MST',
      'America/Los_Angeles': 'PST',
      'America/Anchorage': 'AKST',
      'America/Adak': 'HST',
      'America/Honolulu': 'HST',
      'America/Toronto': 'EST',
      'America/Vancouver': 'PST',
      'America/Edmonton': 'MST',
      'Europe/London': 'GMT',
      'Europe/Paris': 'CET',
      'Europe/Berlin': 'CET',
      'Europe/Rome': 'CET',
      'Europe/Madrid': 'CET',
      'Asia/Dubai': 'GST',
      'Asia/Singapore': 'SGT',
      'Asia/Tokyo': 'JST',
      'Australia/Sydney': 'AEST',
      'Australia/Melbourne': 'AEST',
      'Australia/Perth': 'AWST',
      'America/Mexico_City': 'CST'
    };
    
    // Check if we have a direct mapping
    if (timezoneMap[timezone]) {
      return timezoneMap[timezone];
    }

    // For unknown timezones, try to extract the abbreviation
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
      hour: 'numeric', // Include hour to ensure timezone appears
    });
    
    const formatted = formatter.format(date);
    const match = formatted.match(/[A-Z]{3,4}$/); // Look for 3-4 letter abbr at end
    
    if (match) {
      return match[0];
    }
    
    // If we didn't find a standard abbreviation, extract from GMT format
    const gmtFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
      hour: 'numeric',
    });
    
    const gmtFormatted = gmtFormatter.format(date);
    const gmtMatch = gmtFormatted.match(/GMT[+-]\d{1,2}/);
    
    if (gmtMatch) {
      return gmtMatch[0].replace('GMT', '');
    }
    
    // Last resort: use the region name
    return timezone.split('/').pop()?.replace('_', ' ') || timezone;
  } catch (error) {
    return timezone;
  }
}

// Helper function to check if a timezone is valid
export function isValidTimezone(timezone: string | null | undefined): boolean {
  if (!timezone) return false;
  if (timezone.trim() === '') return false;
  
  try {
    // Check if timezone is valid by trying to create a formatter
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch (e) {
    return false;
  }
}

// Determine which timezone to use for a participant
export function determineEffectiveTimezone(checkIn: any, employee: any): string {
  if (checkIn.timezone_reminder && employee.timezone && isValidTimezone(employee.timezone)) {
    return employee.timezone;
  }
  return checkIn.timezone;
}

// Helper function to calculate the next due date based on check-in schedule
function calculateNextDueDate(checkIn: any): Date {
  const now = new Date();
  const checkInTimezone = checkIn.timezone;
  
  // Get current time in check-in timezone
  const nowInCheckInTz = toZonedTime(now, checkInTimezone);
  
  // Parse cutoff time (e.g., "17:00" for 5pm)
  const cutoffTime = checkIn.cutoff_time;
  let dueDate: Date;
  
  if (checkIn.cadence === 'daily') {
    // For daily, next occurrence is tomorrow at cutoff time
    dueDate = addDays(nowInCheckInTz, 1);
  } else if (checkIn.cadence === 'weekly') {
    // For weekly, find next occurrence of the specified day
    const daysOfWeek = checkIn.days_of_week;
    if (daysOfWeek && daysOfWeek.length > 0) {
      const targetDayName = daysOfWeek[0].toLowerCase();
      const dayMap: { [key: string]: number } = {
        'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
        'thursday': 4, 'friday': 5, 'saturday': 6
      };
      
      const targetDay = dayMap[targetDayName];
      if (targetDay !== undefined) {
        // If today is the target day, check if we're past the cutoff time
        const currentDay = getDay(nowInCheckInTz);
        if (currentDay === targetDay && cutoffTime) {
          const [hours, minutes] = cutoffTime.split(':').map(Number);
          const cutoffToday = setMinutes(setHours(nowInCheckInTz, hours), minutes);
          
          if (nowInCheckInTz < cutoffToday) {
            // Still time today
            dueDate = cutoffToday;
          } else {
            // Past cutoff, next week
            dueDate = nextDay(nowInCheckInTz, targetDay as 0 | 1 | 2 | 3 | 4 | 5 | 6);
          }
        } else {
          // Not the target day, find next occurrence
          dueDate = nextDay(nowInCheckInTz, targetDay as 0 | 1 | 2 | 3 | 4 | 5 | 6);
        }
      } else {
        // Fallback: next week
        dueDate = addDays(nowInCheckInTz, 7);
      }
    } else {
      // No specific day, default to next week
      dueDate = addDays(nowInCheckInTz, 7);
    }
  } else if (checkIn.cadence === 'monthly') {
    // For monthly, next month on the specified day
    const scheduledDay = checkIn.scheduled_day;
    if (scheduledDay) {
      const nextMonth = new Date(nowInCheckInTz.getFullYear(), nowInCheckInTz.getMonth() + 1, scheduledDay);
      dueDate = nextMonth;
    } else {
      dueDate = addDays(nowInCheckInTz, 30);
    }
  } else if (checkIn.cadence === 'one-off') {
    // For one-off, use the specified due date
    if (checkIn.one_off_due_date) {
      dueDate = parseISO(checkIn.one_off_due_date);
    } else {
      dueDate = addDays(nowInCheckInTz, 1);
    }
  } else {
    // Default fallback
    dueDate = addDays(nowInCheckInTz, 2);
  }
  
  // Set the cutoff time if available
  if (cutoffTime && checkIn.cadence !== 'one-off') {
    const [hours, minutes] = cutoffTime.split(':').map(Number);
    dueDate = setMinutes(setHours(dueDate, hours), minutes);
  }
  
  // Return the date as-is since we're working in the check-in timezone
  return dueDate;
}

// Frontend version for preview - generates sample variables
export function generatePreviewVariables(
  participant: any,
  checkIn: any,
  organizationName: string,
  senderName: string
): TemplateVariables {
  const effectiveTimezone = determineEffectiveTimezone(checkIn, participant);
  
  // Calculate the actual next due date based on check-in schedule
  const nextDueDate = calculateNextDueDate(checkIn);
  
  return {
    first_name: participant.first_name || participant.name.split(' ')[0] || participant.name,
    last_name: participant.last_name || participant.name.split(' ').slice(1).join(' ') || '',
    full_name: participant.name,
    check_in_name: checkIn.name,
    check_in_link: '[Check-in Link]',
    due_date: formatInTimeZone(nextDueDate, effectiveTimezone, 'EEEE, MMMM d'),
    due_time: formatInTimeZone(nextDueDate, effectiveTimezone, 'h:mm a'),
    participant_timezone: formatTimezone(effectiveTimezone),
    organization_name: organizationName,
    reminder_sender_name: senderName
  };
} 